import { InputAdornment, TextField, IconButton, Tooltip, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CloseIcon from "@material-ui/icons/Close";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const useTooltipStyles = makeStyles({
  tooltip: {
    fontSize: '14px !important',
    backgroundColor: 'rgba(97, 97, 97, 1) !important',
    padding: '6px 12px !important',
  },
});

const useCalendarStyles = makeStyles({
  '@global': {
    '.react-datepicker__day--today:not(.react-datepicker__day--selected):not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end):not(.react-datepicker__day--in-range)': {
      boxShadow: 'inset 0 0 0 1.5px #084e4e !important',
      borderRadius: '50% !important',
      fontWeight: 'bold !important',
    },
    '.react-datepicker__day--disabled': {
      backgroundColor: '#E2E8F0 !important',
      borderRadius: '50% !important',
      color: '#94A3B8 !important',
      cursor: 'not-allowed !important',
    },
    '.react-datepicker__day--disabled:hover': {
      backgroundColor: '#E2E8F0 !important',
      color: '#94A3B8 !important',
    },
  },
});

const CustomDateInput = React.forwardRef(
  function CustomDateInput(inputProps, ref) {
    const {
      value: inputValue,
      onClick,
      pickerPlaceholder,
      isDisabled,
      isReadOnly,
      showClear,
      tooltipClasses,
      onClear,
      error,
      label,
      required,
      small,
    } = inputProps;
    return (
      <TextField
        ref={ref}
        placeholder={pickerPlaceholder || "DD/MMM/YYYY"}
        style={{
          width: "100%",
          backgroundColor: "transparent",
        }}
        value={inputValue}
        variant="outlined"
        disabled={isDisabled}
        InputProps={{
          readOnly: isReadOnly,
          endAdornment: (
            <InputAdornment
              style={{ cursor: isDisabled || isReadOnly ? "default" : "pointer" }}
              position="end"
            >
              <CalendarTodayIcon
                onClick={isDisabled || isReadOnly ? undefined : onClick}
                style={{ color: isDisabled ? "rgba(0, 0, 0, 0.26)" : "rgba(0, 0, 0, 0.54)" }}
              />
              {showClear && (
                <Tooltip title="Clear date" classes={tooltipClasses}>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    style={{ marginLeft: 4, padding: 4, color: "rgba(0, 0, 0, 0.54)" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ shrink: true }}
        error={!!error}
        onClick={isDisabled || isReadOnly ? undefined : onClick}
        label={required ? <>{label}<span style={{ color: '#d32f2f' }}> *</span></> : label}
        size={small ? "small" : "medium"}
      />
    );
  }
);

export default function CustomDatePicker({
  error,
  value,
  onChange,
  style,
  label,
  required,
  small,
  placeholderText,
  isClearable = false,
  disabled = false,
  readOnly = false,
  ...props
}) {
  let [tempValue, setTempValue] = useState(null);
  const tooltipClasses = useTooltipStyles();
  useCalendarStyles();

  useEffect(() => {
    if (value) {
      const utcDate = new Date(value);
      const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
      setTempValue(localDate);
    } else {
      setTempValue(null);
    }
  }, [value]);

  const hasValue = !!tempValue;

  const handleClear = () => {
    setTempValue(null);
    onChange(null);
  };

  const customInput = (
    <CustomDateInput
      pickerPlaceholder={placeholderText}
      isDisabled={disabled}
      isReadOnly={readOnly}
      showClear={isClearable && !disabled && !readOnly && hasValue}
      tooltipClasses={tooltipClasses}
      onClear={handleClear}
      error={error}
      label={required ? <>{label}<span style={{ color: '#d32f2f' }}> *</span></> : label}
      required={required}
      small={small}
    />
  );

  return (
    <div
      className="custom-date-picker"
      style={{
        ...style,
        overflow: "inherit",
      }}
    >
      <DatePicker
        {...props}
        selected={tempValue}
        disabled={disabled}
        readOnly={readOnly}
        onChange={(value) => {
          if (disabled || readOnly) return;
          setTempValue(value);
          if (value) {
            const utcDate = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
            onChange(utcDate);
          } else {
            onChange(null);
          }
        }}
        customInput={customInput}
        placeholderText={placeholderText || "DD/MMM/YYYY"}
      />
    </div>
  );
}